import React, { useState } from "react";
import { Tooltip } from "antd";
import { ButtonWrapper, CountryPhoneCode, InputContainer, tooltipInnerStyle, Wrapper } from "./style";
import { ReactComponent as VisiblePassword } from "./assets/visible-password.svg";
import { ReactComponent as InvisiblePassword } from "./assets/invisible-password.svg";
import { ReactComponent as InfoIcon } from "../../../assets/icons/info2.svg";
import { useWindowSize } from "@reach/window-size";

interface InputProps {
    label?: string;
    labelColor?: string;
    defaultValue?: string;
    type?: "text" | "number" | "password" | "email" | 'date' | "tel";
    field?: any;
    onBlur?: any
    onClick?: () => void;
    disableLabel?: boolean;
    onInput?: any
    readonly?: boolean;
    disabled?: boolean;
    onChange?: (value: object | string | number) => void;
    placeholder?: string;
    classname?: string;
    hasError?: boolean;
    height?: string;
    width?: string;
    tooltipText?: string;
    id?: string;
    value?: string;
    inputMode?:string;
    triggerTooltipOnInputFocus?: boolean;
    tooltipPlacement?: "right" | "top" | "bottom" | "left";
    showCountryPhoneCode?: boolean;
    paddingForPrefix?: string;
    countryPhoneCode?: string;
}

const PasswordIcon = ({
    isVisible,
    onPasswordToggle,
}: {
    isVisible: boolean;
    onPasswordToggle: () => void;
}) => {
    return (
        <ButtonWrapper type="button" onClick={onPasswordToggle}>
            {isVisible ? <VisiblePassword /> : <InvisiblePassword />}
        </ButtonWrapper>
    );
};

export const Input = ({
    label,
    type = "text",
    field,
    onBlur,
    onInput,
    onClick,
    onChange,
    disableLabel = false,
    readonly = false,
    labelColor,
    defaultValue,
    value,
    classname,
    hasError = false,
    disabled = false,
    height,
    width,
    inputMode,
    tooltipText,
    id = "",
    triggerTooltipOnInputFocus = false,
    tooltipPlacement = "right",
    showCountryPhoneCode = false,
    paddingForPrefix = "",
    countryPhoneCode
}: InputProps) => {
    const {width: deviceWidth} = useWindowSize()
    const [passWordIsVisible, setPassWordIsVisible] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const [passwordTypeState, setPasswordTypeState] = useState("password");
    const onPasswordToggle = () => {
        setPassWordIsVisible(!passWordIsVisible);
        setPasswordTypeState(passWordIsVisible ? "password" : "text");
    };
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const useTooltipOpenProp = triggerTooltipOnInputFocus && hasFocus ? true : tooltipVisible


    const handleInputValue = (id, type, field, value) => {
        if (id?.includes("email")) {
            return field?.value?.trim?.()?.toLowerCase() || value || "";
        } else if (type === "password") {
            return field?.value?.trim?.() || value || "";
        }
        return value !== undefined && value !== null && value !== "" ? value : (field?.value ?? "");
    };

    const inputValue = handleInputValue(id, type, field, value);
    const isFilled =
        (inputValue !== undefined && inputValue !== null && String(inputValue).length > 0) ||
        (defaultValue !== undefined && defaultValue !== null && String(defaultValue).length > 0);

    return (
        <Wrapper
            $labelColor={labelColor}
            $disabled={disabled}
            $height={height}
            $width={width}
            $isReadonly={readonly}
            className={`${classname || ""} ${hasFocus ? "has-focus" : ""} ${hasError ? "error" : ""
                }`}
            onFocus={() => {
                setHasFocus(true);
            }}
            onBlur={() => {
                setHasFocus(false);
            }}
            $paddingForPrefix={paddingForPrefix}
        >
            {!disableLabel && (
                <p
                    className={`input-label ${hasFocus || hasError || isFilled ? "top" : ""}`}
                >
                    {label}
                </p>
            )}
            {showCountryPhoneCode && 
                <CountryPhoneCode>
                    {countryPhoneCode}
                </CountryPhoneCode>
            }
            <InputContainer disabled={disabled}>
                <input
                    autoComplete="new-password"
                    id={id}
                    placeholder={disableLabel ? label : ''}
                    defaultValue={defaultValue}
                    readOnly={readonly}
                    disabled={disabled}
                    onBlur={onBlur}
                    onInput={onInput}
                    type={type === "password" ? passwordTypeState : type}
                    {...field}
                    onClick={onClick && onClick}
                    value={handleInputValue(id, type, field, value)}
                    inputMode={inputMode}
                    onChange={(e) => {
                        field?.onChange(e);
                        if (onChange) {
                            onChange(e);
                        }
                    }}
                    data-testid="input"
                    style={{  pointerEvents: disabled ? 'none' : 'auto' }}
                />
                {tooltipText && (
                    <Tooltip
                        overlayClassName="input-tooltip"
                        placement={tooltipPlacement}
                        title={tooltipText}
                        trigger={["hover", "click"]}
                        open={useTooltipOpenProp}
                        onOpenChange={setTooltipVisible}
                        overlayInnerStyle={tooltipInnerStyle}
                    >
                        <InfoIcon width={16} height={16}/>
                    </Tooltip>
                )}
            </InputContainer>

            {type === "password" && (
                <PasswordIcon
                    isVisible={passWordIsVisible}
                    onPasswordToggle={onPasswordToggle}
                />
            )}
        </Wrapper>
    );
};
